// „Abschließen und senden“ (E2, Schritt 4; Mockup /doku/senden): alles auf
// einen Blick – Stationen, Fotos, Notizen, Sprachmemos, Material (seit E3b;
// Längen nur, wo es welche gibt), Dachplanung – und dann mit einem Tipp ans
// Büro. Die Besichtigung ist die ganze Zeit
// schon am Gerät gespeichert; „An das Büro senden“ schließt sie ab und
// stellt den Abschluss hinten in den Postausgang (arbeitskopie.ts,
// `abschliessen`). Ohne Empfang geht das genauso – sie wartet dann am Gerät.
//
// Senden geht immer – sind nicht alle sieben Stationen fertig, steht mit
// Links da, welche, und „An das Büro senden“ fragt erst nach: „Trotzdem
// senden?“ (Gernot, 19.10.2026 abends: Kommt man an eine Station nicht
// hin, muss Senden trotzdem gehen; vorher war Senden bis dahin gesperrt).
// Seit 20.10.2026 (Gernots Augentest) ist die Rückfrage ein eigenes Fenster
// über der Seite, unten führt „‹ Übersicht“ zurück, und „Sonstiges“ steht
// in der Karte.
// Die Station bleibt dann „nicht fertig“ – so sieht es das Büro. Die
// Dachplanung ist keine Bedingung, sie darf nachkommen. Solange ein
// Sprachmemo aufgenommen (oder gespeichert) wird, wartet der Knopf – es soll
// mit. Ist die Besichtigung schon gesendet, zeigt die Seite das statt des
// Knopfs. Gezählt wird nur die erste Sendung – was nachgereicht wurde (seit
// Schritt 5), zeigt die Seite des Nachtrags (NachtragSeite.tsx), die diese
// Karte mitbenutzt.
//
// Adresse: #/aufnahme/<kennung>/senden

import { Fragment, useEffect, useRef, useState } from "react";
import { LAENGEN_ARTEN, STATIONEN, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, fehlerMelden, Ikon, Spaeter, Tipp, toastZeigen, useNetz } from "@happy-energy/oberflaeche";
import { abschliessen, aenderbar, meterZeigen } from "../arbeitskopie.ts";
import { STATIONEN_ZAHL, stationenFertig } from "../postausgang.ts";
import { dauerZeigen, useMemoAufnahme } from "../memo.ts";
import { fortschrittText, uebernahmeStand } from "../vom-geraet.ts";
import { useUebernahmen } from "./fotos.tsx";
import type { Foto, Memo, Notiz, Station } from "../speicher.ts";
import { mitLaengen } from "../material.ts";
import { aufnahmeLesen, materialZeile, type AufnahmeGanz } from "./Uebersicht.tsx";
import { anzahl, SendeStand, useLive, usePostausgang, ZUM_START } from "./gemeinsam.tsx";
import { gezeichnet } from "./fotos.tsx";

/** Eine Zeile der Karte: links was, rechts wie viel – „ok“ mit Haken, „offen“ in Orange. */
export interface Zeile {
  was: string;
  wert: string;
  ok?: boolean;
  offen?: boolean;
}

/** Die Karte „alles auf einen Blick“: links was, rechts wie viel. */
export function ZusammenKarte({ zeilen }: { zeilen: readonly Zeile[] }) {
  return (
    <div className="zusammen-karte">
      {zeilen.map((z) => (
        <div key={z.was} className={`zusammen-zeile${z.offen ? " offen" : ""}`}>
          <span>{z.was}</span>
          <b>
            {z.wert}
            {z.ok && <Ikon name="haken" groesse="k" />}
          </b>
        </div>
      ))}
    </div>
  );
}

/** Wie die Fotos in der Karte stehen: „keine“, „3“, „3, davon 1 gezeichnet“, „2, alle gezeichnet“. */
export function fotosText(fotos: readonly Foto[]): string {
  const gez = fotos.filter(gezeichnet).length;
  const n = fotos.length;
  return n === 0 ? "keine" : gez === 0 ? String(n) : gez === n ? (n === 1 ? "1, gezeichnet" : `${n}, alle gezeichnet`) : `${n}, davon ${gez} gezeichnet`;
}

/** Wie die Notizen (die es „gibt“) in der Karte stehen: „keine“, „2 zur Station, 1 zu einem Foto“. */
export function notizenText(notizen: readonly Notiz[]): string {
  const zurStation = notizen.filter((x) => x.stationId !== null).length;
  const zuFotos = notizen.filter((x) => x.fotoId !== null).length;
  return (
    [zurStation > 0 ? `${zurStation} zur Station` : "", zuFotos > 0 ? anzahl(zuFotos, "1 zu einem Foto", "# zu Fotos") : ""].filter(Boolean).join(", ") ||
    "keine"
  );
}

/** Wie die Sprachmemos in der Karte stehen: „keine“ oder „2 · zusammen 0:40“ – nur fertige. */
export function memosText(memos: readonly Memo[]): string {
  const fertig = memos.filter((m) => m.pruefsumme !== null);
  return fertig.length === 0 ? "keine" : `${fertig.length} · zusammen ${dauerZeigen(fertig.reduce((summe, m) => summe + m.dauerSekunden, 0))}`;
}

/**
 * Was in der Karte steht – aus der Arbeitskopie gezählt, mit denselben
 * Regeln wie beim Senden: nur die erste Sendung, ohne Nachgereichtes.
 */
export function zusammenfassung(alles: AufnahmeGanz): Zeile[] {
  const erste = <T extends { nachtragId: string | null }>(liste: T[]) => liste.filter((x) => (x.nachtragId ?? null) === null);
  const ganz = {
    ...alles,
    fotos: erste(alles.fotos),
    notizen: erste(alles.notizen),
    memos: erste(alles.memos),
    laengen: erste(alles.laengen),
    materialangaben: erste(alles.materialangaben),
  };
  const fertig = stationenFertig(ganz.stationen);
  const fotos = fotosText(ganz.fotos);

  // Eine Notiz „gibt es“, wenn sie Text oder ein fertiges Sprachmemo trägt.
  const memos = ganz.memos.filter((m) => m.pruefsumme !== null);
  const mitMemo = new Set(memos.map((m) => m.notizId));
  const notizen = ganz.notizen.filter((x) => x.text.trim() !== "" || mitMemo.has(x.id));
  const notizText = notizenText(notizen);

  const memoText = memosText(memos);

  const laengen = LAENGEN_ARTEN.flatMap((art) => {
    const summe = ganz.laengen.filter((l) => l.laengenArt === art.schluessel && l.meter !== null).reduce((s, l) => s + (l.meter ?? 0), 0);
    return summe > 0 ? [`${art.name} ${meterZeigen(Math.round(summe * 100) / 100)} ${art.einheit}`] : [];
  });

  // „Sonstiges“ ist keine Notiz, aber genauso Text fürs Büro (seit 20.10.2026, Gernots Augentest: es fehlte hier).
  const sonstiges = ganz.stationen.filter((s) => s.sonstiges?.trim()).length;

  const k2 = ganz.k2;
  return [
    { was: "Stationen", wert: `${fertig} von ${STATIONEN_ZAHL}`, ok: fertig === STATIONEN_ZAHL, offen: fertig < STATIONEN_ZAHL },
    { was: "Fotos", wert: fotos },
    { was: "Notizen", wert: notizText },
    { was: "Sprachmemos", wert: memoText },
    // Seit E3b: Material von draußen; Längen nur noch, wo die Besichtigung welche hat (vor dem Update begonnen).
    { was: "Material", wert: ganz.materialangaben.length === 0 ? "keines – das Büro ergänzt" : materialZeile(ganz.materialangaben, true) },
    ...(mitLaengen(alles.laengen) ? [{ was: "Längen", wert: laengen.join(" · ") || "keine" }] : []),
    { was: "Sonstiges", wert: sonstiges === 0 ? "keines" : anzahl(sonstiges, "bei 1 Station", "bei # Stationen") },
    k2?.pruefsumme
      ? { was: "K2-PDF", wert: "abgelegt", ok: true }
      : k2?.uebersprungen
        ? { was: "K2-PDF", wert: "übersprungen" }
        : { was: "K2-PDF", wert: "noch offen – kann nachkommen" },
    // Seit E3c (S5): die Liste von K2 (Excel-Report) für den Lieferanten.
    k2?.excel ? { was: "K2-Liste", wert: "abgelegt", ok: true } : { was: "K2-Liste", wert: "noch offen – kann nachkommen" },
  ];
}

/**
 * Seit E3c (S5; O94): Es werden noch Fotos übernommen – „Vom Gerät“ mit „3
 * von 8“ oder ein Foto aus der Kamera. Senden wartet darauf (auf dieser
 * Seite und bei „Nachtrag senden“), sonst ginge die Besichtigung ohne sie.
 */
export function FotosInArbeit({ aufnahmeId }: { aufnahmeId: string }) {
  const stand = uebernahmeStand(useUebernahmen(), aufnahmeId);
  return (
    <Tipp art="gelb" ikon="bilder">
      <b>{stand ? fortschrittText(stand.fertig, stand.gesamt) : "Ein Foto wird noch vorbereitet …"}</b> Es soll mit – senden geht gleich danach.
    </Tipp>
  );
}

function SendenInhalt({ ich, ganz }: { ich: IchAntwort; ganz: AufnahmeGanz }) {
  const { aufnahme, stationen } = ganz;
  const post = usePostausgang();
  const netz = useNetz();
  const memo = useMemoAufnahme();
  const [sendet, setSendet] = useState(false);
  // „Trotzdem senden?“ – nur, wenn nicht alle Stationen fertig sind.
  const [nachfrage, setNachfrage] = useState(false);

  const zurUebersicht = `#/aufnahme/${aufnahme.id}`;
  const zuGesendet = `#/aufnahme/${aufnahme.id}/gesendet`;
  const schonGesendet = !aenderbar(aufnahme);
  const offen = stationen.filter((s) => !s.fertig);
  // Ein Memo wird aufgenommen (oder nach dem Stopp noch gespeichert): Es soll mit.
  const memoLaeuft = memo.art !== "aus" || ganz.memos.some((m) => m.pruefsumme === null);
  // Seit E3c (S5; O94): Werden noch Fotos übernommen – auch nach dem Verlassen der Station –, wartet „Senden“.
  const fotosLaufen = useUebernahmen().some((u) => u.aufnahmeId === aufnahme.id);
  const bereit = !schonGesendet && !memoLaeuft && !fotosLaufen;

  function antippen() {
    if (!bereit) return;
    if (offen.length > 0 && !nachfrage) {
      setNachfrage(true);
      return;
    }
    void senden(offen.length > 0);
  }

  async function senden(auchUnfertig: boolean) {
    setSendet(true);
    try {
      const ergebnis = await abschliessen(aufnahme.id, new Date(), { auchUnfertig });
      if (ergebnis === "abgeschlossen" || ergebnis === "gesperrt") {
        window.location.hash = zuGesendet;
        return;
      }
      toastZeigen(ergebnis === "nicht-fertig" ? "Noch ist nicht jede Station fertig." : "Ein Sprachmemo wird noch gespeichert – einen Moment.", "fehler");
    } catch (fehler) {
      fehlerMelden("doku", fehler);
      toastZeigen("Konnte nicht abschließen – bitte noch einmal.", "fehler");
    } finally {
      setSendet(false);
    }
  }

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={{ adresse: zurUebersicht, name: "Übersicht" }}
        titel="Abschließen"
        rechts={<SendeStand wartend={post.wartend} />}
        gross={schonGesendet ? "Schon gesendet" : offen.length === 0 ? "Alles beisammen" : "Noch nicht ganz"}
        unter={`${aufnahme.kunde.name} · ${aufnahme.objekt.strasse}`}
      />
      <main className="app-inhalt">
        <ZusammenKarte zeilen={zusammenfassung(ganz)} />

        {schonGesendet ? (
          <Tipp art="grau" ikon="haken">
            {aufnahme.stand === "im_buero"
              ? "Diese Besichtigung ist schon im Büro – hier gibt es nichts mehr zu senden."
              : "Diese Besichtigung ist schon abgeschlossen und geht von selbst ans Büro."}
          </Tipp>
        ) : memoLaeuft ? (
          <Tipp art="gelb" ikon="mikro">
            <b>Ein Sprachmemo wird noch aufgenommen oder gespeichert.</b> Es soll mit – senden geht gleich danach.
          </Tipp>
        ) : fotosLaufen ? (
          <FotosInArbeit aufnahmeId={aufnahme.id} />
        ) : offen.length > 0 ? (
          <Tipp art="gelb" ikon="achtung">
            <b>Noch nicht fertig:</b>{" "}
            {offen.map((s, i) => (
              <Fragment key={s.id}>
                {i > 0 && ", "}
                <a href={`${zurUebersicht}/station/${s.nr}`}>{STATIONEN.find((x) => x.nr === s.nr)?.name ?? s.art}</a>
              </Fragment>
            ))}
. Antippen und fertig machen – oder trotzdem senden, wenn es dort nicht weitergeht.
          </Tipp>
        ) : !netz ? (
          <Tipp art="gelb" ikon="offline">
            <b>Kein Empfang – kein Problem.</b> Die Besichtigung ist sicher am Gerät und geht von selbst raus, sobald wieder Netz da
            ist. Die App muss dafür nur offen bleiben.
          </Tipp>
        ) : (
          <Tipp ikon="wolke">
            Fotos werden am Handy verkleinert, damit sie auch bei schwachem Empfang schnell drüben sind. Der Standort wird aus den
            Fotos entfernt – die Adresse steht beim Objekt.
          </Tipp>
        )}

        <Spaeter titel="Das Büro bekommt eine Nachricht" wann="Stufe 2">
          Heute erscheint die Besichtigung einfach im Büro unter „Zu tun“. Später mit Benachrichtigung und Stand.
        </Spaeter>
      </main>
      <AppFuss>
        {schonGesendet ? (
          <a href={zuGesendet} className="knopf-haupt knopf-block">
            <Ikon name="haken" /> Gesendet – Stand ansehen
          </a>
        ) : (
          // Links zurück zur Übersicht, wie in den Stationen (seit 20.10.2026, Gernots Augentest – vorher ging es nur oben zurück).
          <div className="knopf-reihe">
            <a href={zurUebersicht} className="knopf-rand knopf-vorige" aria-label="Zurück zur Übersicht der Stationen">
              <Ikon name="links" />
              <span>Übersicht</span>
            </a>
            <button type="button" className="knopf-haupt knopf-naechste" disabled={!bereit || sendet} onClick={antippen}>
              <Ikon name="senden" /> {netz ? "An das Büro senden" : "Senden, sobald Empfang da ist"}
            </button>
          </div>
        )}
      </AppFuss>
      {nachfrage && offen.length > 0 && !schonGesendet && (
        <TrotzdemSenden offen={offen} sendet={sendet} senden={antippen} zurueck={() => setNachfrage(false)} />
      )}
    </div>
  );
}

/**
 * Die Rückfrage, wenn nicht alle Stationen fertig sind: ein eigenes Fenster
 * über der Seite, nicht zu übersehen – auch für jemanden, der es eilig hat
 * (Gernot, 20.10.2026: vorher wurde nur der gelbe Kasten fett, die Seite
 * blieb fast gleich). Nur das Nötige: welche Stationen offen sind und was
 * das im Büro heißt. „Zurück“ schließt es; „Trotzdem senden“ sendet.
 */
function TrotzdemSenden({
  offen,
  sendet,
  senden,
  zurueck,
}: {
  offen: readonly Station[];
  sendet: boolean;
  senden: () => void;
  zurueck: () => void;
}) {
  const zurueckKnopf = useRef<HTMLButtonElement>(null);
  const zurueckAktuell = useRef(zurueck);
  useEffect(() => {
    zurueckAktuell.current = zurueck;
  });
  // Einmal beim Öffnen: Der sichere Weg hat den Fokus; Escape (Tastatur am Laptop) schließt.
  useEffect(() => {
    zurueckKnopf.current?.focus();
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") zurueckAktuell.current();
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);

  return (
    <div className="nachfrage-huelle">
      <div className="nachfrage-fenster" role="alertdialog" aria-modal="true" aria-labelledby="nachfrage-titel" aria-describedby="nachfrage-text">
        <span className="nachfrage-zeichen" aria-hidden="true">
          <Ikon name="achtung" groesse="g" />
        </span>
        <h2 id="nachfrage-titel">Noch nicht alles fertig</h2>
        <div id="nachfrage-text" className="nachfrage-text">
          <p>{offen.length === 1 ? "Diese Station ist noch offen:" : `Diese ${offen.length} Stationen sind noch offen:`}</p>
          <ul>
            {offen.map((s) => (
              <li key={s.id}>{STATIONEN.find((x) => x.nr === s.nr)?.name ?? s.art}</li>
            ))}
          </ul>
          <p>Im Büro steht dann, welche Station nicht fertig ist.</p>
        </div>
        <button type="button" className="knopf-dunkel knopf-block" disabled={sendet} onClick={senden}>
          <Ikon name="senden" /> Trotzdem senden
        </button>
        <button ref={zurueckKnopf} type="button" className="knopf-rand knopf-block" disabled={sendet} onClick={zurueck}>
          Zurück
        </button>
      </div>
    </div>
  );
}

export function SendenSeite({ ich, aufnahmeId }: { ich: IchAntwort; aufnahmeId: string }) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  if (ganz === undefined) return <div className="app-seite" />;
  if (ganz === null) {
    return (
      <div className="app-seite">
        <AppKopf umgebung={ich.umgebung} zurueck={ZUM_START} titel="Abschließen" gross="Nicht gefunden" />
        <main className="app-inhalt">
          <Tipp art="grau">Diese Besichtigung ist auf diesem Gerät nicht gespeichert.</Tipp>
        </main>
      </div>
    );
  }
  return <SendenInhalt ich={ich} ganz={ganz} />;
}
